import React, { FC, useMemo } from 'react';

import { PREFERENCE_MAX_SELECTED_COUNT } from '../../../../../constants/preferences';

import { SelectedItem, SelectedItemsType } from '../types';

import SelectedChip from './SelectedChip';
import { getSelectedItems } from './helpers';
import * as Styled from './styled';

export type Props = {
  items: [SelectedItemsType, SelectedItemsType?];
};

/**
 * Preview of selected items
 *
 * @param props       Props passed to the component
 * @param props.items Array of items data (selected items and updaters)
 * @returns           The component itself
 *
 * @summary For assignment it will be 2 elements, for labels just one
 */
const SelectedItems: FC<Props> = ({ items }) => {
  const [primaryItems, secondaryItems] = items;

  const selectedItemsCount =
    primaryItems.items.length + (secondaryItems?.items?.length ?? 0);

  if (
    selectedItemsCount > PREFERENCE_MAX_SELECTED_COUNT ||
    selectedItemsCount === 0
  ) {
    return null;
  }

  const selectedItems: SelectedItem[] = useMemo(() => {
    const selectedItemsPrimary = getSelectedItems(
      primaryItems.items,
      primaryItems.updater,
    );

    if (
      selectedItemsPrimary.length === PREFERENCE_MAX_SELECTED_COUNT ||
      !secondaryItems
    ) {
      return selectedItemsPrimary;
    }

    const selectedItemsSecondary = getSelectedItems(
      secondaryItems.items,
      secondaryItems.updater,
      true,
    );

    return [...selectedItemsPrimary, ...selectedItemsSecondary].slice(
      0,
      PREFERENCE_MAX_SELECTED_COUNT,
    );
    // Update this list only when selected items count is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemsCount]);

  return (
    <Styled.StyledList>
      {selectedItems.map(item => (
        <SelectedChip item={item} key={item.label} />
      ))}
    </Styled.StyledList>
  );
};

export default SelectedItems;
